import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast from 'react-hot-toast';
import { hasPermission } from '../../../config/rbac';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const CommsTab = ({ claim, claimId, notes, setNotes, S }) => {
  const { t } = useTranslation();
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);
  const loggedinUser = useSelector(state => state.loggedinUser);
  const myName = loggedinUser?.userInfo?.data?.data?.name || t('screens.claimsScreen.detail.actorSystem');
  const inputRef = useRef(null);
  // Reuse the claims.note gate — read-only roles (complianceOfficer,
  // underwriter, adjuster-below-senior) cannot post messages.
  const userRole = useSelector(state => {
    const info = state?.userLogin?.userInfo?.data;
    return info?.data?.role || info?.user?.role;
  });
  const canSend = hasPermission(userRole, 'claims', 'note');

  // Communications = notes where internal === false
  const messages = notes.filter(n => n.internal === false);

  const handleSend = async () => {
    const text = msg.trim();
    if (!text) return;
    if (!claimId) {
      toast.error(t('screens.claimsScreen.detail.claimNotLoaded'));
      return;
    }
    setSending(true);
    try {
      const resp = await axios.post(`${BASE_URL}/claims/${claimId}/notes`, { text, internal: false }, { withCredentials: true });
      setNotes(resp.data.data || []);
      setMsg('');
      inputRef.current?.focus();
    } catch (err) {
      toast.error(err.response?.data?.msg || t('screens.claimsScreen.detail.sendFailed'));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 380px)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='#10b981' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
          <rect x='3' y='11' width='18' height='11' rx='2' ry='2' />
          <path d='M7 11V7a5 5 0 0 1 10 0v4' />
        </svg>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#10b981' }}>{t('screens.claimsScreen.detail.loggedToRecord')}</span>
        <span style={{ fontSize: 12, color: S.textMuted }}>{t('screens.claimsScreen.detail.commsOn', { id: claim?.id || claimId })}</span>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
        {messages.length === 0 && (
          <div style={{ padding: '40px 0', textAlign: 'center', color: S.textMuted, fontSize: 13 }}>{t('screens.claimsScreen.detail.noMessages')}</div>
        )}
        {messages.map((m, i) => {
          const isSelf = m.authorName === myName;
          const initials = (m.authorName || '?')
            .split(' ')
            .map(p => p[0])
            .join('')
            .slice(0, 2)
            .toUpperCase();
          const timeStr = m.createdAt ? new Date(m.createdAt).toLocaleString() : '';
          return (
            <div key={m._id || i} style={{ display: 'flex', gap: 10, justifyContent: isSelf ? 'flex-end' : 'flex-start' }}>
              {!isSelf && (
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {initials}
                </div>
              )}
              <div
                style={{
                  maxWidth: '65%',
                  background: isSelf ? `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})` : S.bgCard,
                  color: isSelf ? '#fff' : S.textPrimary,
                  borderRadius: 12,
                  padding: '12px 16px',
                  border: isSelf ? 'none' : `1px solid ${S.borderLight}`,
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4, opacity: isSelf ? 0.8 : 1, color: isSelf ? '#fff' : S.textMuted }}>
                  {m.authorName || '—'} — {timeStr}
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.5 }}>{m.text}</div>
              </div>
            </div>
          );
        })}
      </div>
      {canSend && (
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            ref={inputRef}
            value={msg}
            onChange={e => setMsg(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('screens.claimsScreen.detail.messagePlaceholder')}
            style={{
              flex: 1,
              padding: '12px 16px',
              background: '#fff',
              border: `1px solid ${S.borderLight}`,
              borderRadius: 10,
              fontSize: 13,
              color: S.textPrimary,
              outline: 'none',
              fontFamily: 'inherit',
            }}
          />
          <button
            onClick={handleSend}
            disabled={!msg.trim() || sending}
            style={{
              padding: '12px 20px',
              background: `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})`,
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 600,
              cursor: msg.trim() && !sending ? 'pointer' : 'not-allowed',
              opacity: msg.trim() && !sending ? 1 : 0.6,
            }}
          >
            {sending ? '…' : t('screens.claimsScreen.detail.send')}
          </button>
        </div>
      )}
    </div>
  );
}

export default CommsTab